import React, { useCallback, useEffect, useState } from "react";
import { fetchBillingInfo, createBillingPortalSession } from "../../utils/api";
import "./BillingInfoCard.css";

const formatMoney = (amountCents, currency) => {
  if (amountCents === null || amountCents === undefined) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: String(currency || "usd").toUpperCase(),
  }).format(Number(amountCents) / 100);
};

const formatDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const BillingInfoCard = ({ locationId }) => {
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [error, setError] = useState("");

  const loadInfo = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchBillingInfo({ locationId });
      setInfo(data);
      setError("");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load billing information.",
      );
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    void loadInfo();
  }, [loadInfo]);

  const handleManagePaymentMethod = async () => {
    setOpeningPortal(true);
    setError("");
    try {
      const result = await createBillingPortalSession({ locationId });
      const url = String(result?.url || "").trim();
      if (url) {
        window.location.href = url;
        return;
      }
      throw new Error("Billing portal session did not return a redirect URL.");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to open billing portal.",
      );
    } finally {
      setOpeningPortal(false);
    }
  };

  if (loading) {
    return (
      <div className="ai-card billing-info-card">
        <p className="ai-help">Loading billing information...</p>
      </div>
    );
  }

  return (
    <div className="ai-card billing-info-card">
      <div className="ai-card-header">
        <div>
          <p className="ai-card-note">Billing Information</p>
          <h3 className="ai-card-title">Payment method &amp; upcoming renewals</h3>
        </div>
        {info?.hasCustomer ? (
          <button
            type="button"
            className="ai-button ai-button-secondary ai-button-outline ai-button-sm"
            onClick={handleManagePaymentMethod}
            disabled={openingPortal}
          >
            {openingPortal ? "Opening..." : "Manage Payment Method"}
          </button>
        ) : null}
      </div>

      {error ? <div className="ai-alert ai-alert-error">{error}</div> : null}

      {!info?.hasCustomer ? (
        <p className="ai-help">
          No billing information yet — purchase a plan to get started.
        </p>
      ) : (
        <div className="billing-info-grid">
          <div className="billing-info-item">
            <span className="billing-info-label">Card on file</span>
            <span className="billing-info-value">
              {info.paymentMethod
                ? `${info.paymentMethod.brand?.toUpperCase() || "Card"} •••• ${info.paymentMethod.last4} (exp ${info.paymentMethod.expMonth}/${info.paymentMethod.expYear})`
                : "No card on file"}
            </span>
          </div>
          <div className="billing-info-item">
            <span className="billing-info-label">Billing email</span>
            <span className="billing-info-value">{info.billingEmail || "--"}</span>
          </div>
          {info.renewals?.length ? (
            info.renewals.map((renewal) => (
              <div className="billing-info-item" key={renewal.productType}>
                <span className="billing-info-label">
                  Next renewal — {renewal.productLabel}
                </span>
                <span className="billing-info-value">
                  {formatDate(renewal.nextRenewalDate)} ·{" "}
                  {formatMoney(renewal.amount, renewal.currency)}
                </span>
              </div>
            ))
          ) : (
            <div className="billing-info-item">
              <span className="billing-info-label">Active subscriptions</span>
              <span className="billing-info-value">None</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BillingInfoCard;
